import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import {
  getFamily,
  inviteFamilyMember,
  removeFamilyMember,
  generateReferralCode,
  getReferralStats,
  assignFamilyAccount,
  getFamilyMemberBudgets,
  getConnectedAccounts
} from '../services/api';
import { colors } from '../styles';

const FamilyInviteSection = () => {
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [isPrimary, setIsPrimary] = useState(true);
  const [inviteRole, setInviteRole] = useState('spouse');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteError, setInviteError] = useState(null);
  const [inviteSuccess, setInviteSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [referralLink, setReferralLink] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);
  const [accountSelections, setAccountSelections] = useState({});
  const [memberBudgets, setMemberBudgets] = useState({});
  const [budgetsLoading, setBudgetsLoading] = useState({});
  const [plaidAccounts, setPlaidAccounts] = useState([]);

  const currencyFormatter = useMemo(
    () => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }),
    []
  );

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const response = await getFamily();
        const family = response?.data?.family || { familyMembers: [] };
        setMembers(family.familyMembers || []);
        setIsPrimary(response?.data?.isPrimary !== false);
        const accounts = {};
        (family.familyMembers || []).forEach((member) => {
          accounts[String(member._id)] = '';
        });
        setAccountSelections(accounts);
      } catch (error) {
        setInviteError(error?.response?.data?.error || 'Unable to load family data.');
      } finally {
        setLoading(false);
      }
    };

    const loadAccounts = async () => {
      try {
        // Unified endpoint returns flat array of accounts
        const accounts = await getConnectedAccounts();
        // Transform to format needed by this component
        const formattedAccounts = (accounts || []).map((account) => ({
          id: account.accountId || account.id,
          name: account.name,
          institution: account.institution?.name || 'Unknown Institution'
        }));
        setPlaidAccounts(formattedAccounts);
      } catch (error) {
        console.error('Failed to fetch accounts', error);
      }
    };

    const loadReferral = async () => {
      try {
        setReferralLoading(true);
        const stats = await getReferralStats();
        if (stats?.shareUrl) {
          setReferralLink(stats.shareUrl);
          return;
        }
        const generated = await generateReferralCode();
        setReferralLink(generated?.shareUrl || null);
      } catch (_) {
        setReferralLink(null);
      } finally {
        setReferralLoading(false);
      }
    };

    load();
    loadAccounts();
    loadReferral();
  }, []);

  const activeMembers = useMemo(
    () => members.filter((member) => member.status !== 'inactive'),
    [members]
  );

  const spouseLocked = useMemo(
    () => activeMembers.some((member) => member.role === 'spouse'),
    [activeMembers]
  );

  const handleInvite = async () => {
    setInviteError(null);
    setInviteSuccess(null);

    if (!inviteEmail.trim()) {
      setInviteError('Email is required to send an invitation.');
      return;
    }

    if (inviteRole === 'spouse' && spouseLocked) {
      setInviteError('You already have a spouse connected. Remove them before inviting another.');
      return;
    }

    try {
      setSubmitting(true);
      const response = await inviteFamilyMember({
        email: inviteEmail.trim(),
        name: inviteName.trim(),
        role: inviteRole
      });

      const updated = response?.data?.family?.familyMembers;
      if (updated) {
        setMembers(updated);
      }
      setInviteName('');
      setInviteEmail('');
      setInviteSuccess('Invitation sent! Your family member will receive an email shortly.');
    } catch (error) {
      const code = error?.response?.data?.code;
      if (code === 'PREMIUM_REQUIRED') {
        setInviteError('Family invitations require an active premium subscription.');
      } else if (code === 'SPOUSE_EXISTS') {
        setInviteError('A spouse is already invited or active.');
      } else if (code === 'NOT_PRIMARY') {
        setInviteError('Only the primary account holder can invite family members.');
      } else {
        setInviteError(error?.response?.data?.error || 'Unable to send invitation.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (member) => {
    if (!member._id) return;
    try {
      await removeFamilyMember(member._id);
      setMembers((prev) =>
        prev.map((entry) =>
          entry._id === member._id ? { ...entry, status: 'inactive' } : entry
        )
      );
      setInviteSuccess('Family member removed.');
    } catch (error) {
      setInviteError(error?.response?.data?.error || 'Failed to remove family member.');
    }
  };

  const handleAccountSelection = (memberId, value) => {
    setAccountSelections((prev) => ({
      ...prev,
      [memberId]: value
    }));
  };

  const saveAccountAssignment = async (memberId) => {
    const selection = accountSelections[memberId];
    if (!selection) {
      setInviteError('Select an account to assign first.');
      return;
    }
    try {
      const response = await assignFamilyAccount(memberId, {
        plaidAccountId: selection
      });
      setMembers(response.familyMembers || response?.family?.familyMembers || members);
      setInviteSuccess('Account assigned to family member');
    } catch (error) {
      setInviteError(error?.response?.data?.error || 'Unable to assign account');
    }
  };

  const loadBudgetsForMember = async (memberId) => {
    setBudgetsLoading((prev) => ({ ...prev, [memberId]: true }));
    try {
      const response = await getFamilyMemberBudgets(memberId);
      setMemberBudgets((prev) => ({
        ...prev,
        [memberId]: response?.data?.budgets || []
      }));
    } catch (error) {
      setInviteError(error?.response?.data?.error || 'Unable to load budgets');
    } finally {
      setBudgetsLoading((prev) => ({ ...prev, [memberId]: false }));
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        borderRadius: 4,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider'
      }}
    >
      <Stack direction="row" spacing={2.5} alignItems="center" sx={{ mb: 3 }}>
        <Box
          aria-hidden
          sx={{
            width: 56,
            height: 56,
            flexShrink: 0,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `linear-gradient(145deg, ${colors.primary}18 0%, ${colors.secondary}14 100%)`,
            border: `1px solid ${colors.primary}40`,
            boxShadow: '0 1px 2px rgba(20, 20, 59, 0.06)',
          }}
        >
          <FamilyRestroomIcon
            sx={{
              fontSize: 30,
              color: colors.primary,
              opacity: 0.95,
            }}
          />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight="bold">
            Invite your family for free
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Share budgets, categories, and transaction alerts with your spouse and kids without
            paying for extra seats.
          </Typography>
        </Box>
      </Stack>

      {!isPrimary && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Family invitations can only be sent by the primary account holder.
        </Alert>
      )}

      {inviteError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setInviteError(null)}>
          {inviteError}
        </Alert>
      )}

      {inviteSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInviteSuccess(null)}>
          {inviteSuccess}
        </Alert>
      )}

      <Stack spacing={2} sx={{ mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Family member name"
            placeholder="Optional"
            value={inviteName}
            onChange={(event) => setInviteName(event.target.value)}
            fullWidth
          />
          <TextField
            label="Email"
            value={inviteEmail}
            onChange={(event) => setInviteEmail(event.target.value)}
            required
            fullWidth
          />
        </Stack>

        <ToggleButtonGroup
          exclusive
          value={inviteRole}
          onChange={(_, value) => value && setInviteRole(value)}
          color="primary"
          size="small"
        >
          <ToggleButton value="spouse" disabled={spouseLocked}>
            Spouse
          </ToggleButton>
          <ToggleButton value="child">Child</ToggleButton>
        </ToggleButtonGroup>

        <Button
          variant="contained"
          size="large"
          onClick={handleInvite}
          disabled={!isPrimary || submitting}
        >
          {submitting ? 'Sending invite…' : 'Send invitation'}
        </Button>
      </Stack>

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
        Active family members
      </Typography>

      {loading ? (
        <Typography variant="body2" color="text.secondary">
          Loading family members…
        </Typography>
      ) : activeMembers.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No family members yet. Send an invite to get started!
        </Typography>
      ) : (
        <Stack spacing={2}>
          {activeMembers.map((member) => {
            const memberId = String(member._id);
            const assignedAccounts = member.assignedAccounts || [];
            const budgets = memberBudgets[memberId] || [];
            return (
              <Paper
                key={member._id || member.email}
                variant="outlined"
                sx={{ p: 2, borderRadius: 3 }}
              >
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {member.name || member.email || 'Family member'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {member.email || 'Email pending'}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Chip
                          icon={<FamilyRestroomIcon fontSize="small" />}
                          label={member.role === 'spouse' ? 'Spouse' : 'Child'}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                        <Chip
                          label={member.status === 'active' ? 'Active' : 'Invited'}
                          color={member.status === 'active' ? 'success' : 'warning'}
                          size="small"
                        />
                      </Stack>
                    </Stack>
                    <Button
                      variant="text"
                      color="error"
                      startIcon={<PersonRemoveIcon />}
                      onClick={() => handleRemove(member)}
                    >
                      Remove
                    </Button>
                  </Stack>

                  <Divider />

                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      Assigned accounts
                    </Typography>
                    {assignedAccounts.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No accounts assigned yet.
                      </Typography>
                    ) : (
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {assignedAccounts.map((account) => (
                          <Chip key={account.plaidAccountId} label={account.accountName || account.plaidAccountId} />
                        ))}
                      </Stack>
                    )}
                    {plaidAccounts.length > 0 && (
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                        <Select
                          value={accountSelections[memberId] || ''}
                          displayEmpty
                          onChange={(event) => handleAccountSelection(memberId, event.target.value)}
                          fullWidth
                        >
                          <MenuItem value="">
                            <em>Select account</em>
                          </MenuItem>
                          {plaidAccounts.map((account) => (
                            <MenuItem key={account.id} value={account.id}>
                              {account.name} {account.institution ? `· ${account.institution}` : ''}
                            </MenuItem>
                          ))}
                        </Select>
                        <Button
                          variant="outlined"
                          onClick={() => saveAccountAssignment(memberId)}
                          disabled={!isPrimary}
                        >
                          Assign account
                        </Button>
                      </Stack>
                    )}
                  </Stack>

                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Budgets
                      </Typography>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => loadBudgetsForMember(memberId)}
                        disabled={budgetsLoading[memberId]}
                      >
                        {budgetsLoading[memberId] ? 'Loading…' : 'Refresh'}
                      </Button>
                    </Stack>
                    {budgetsLoading[memberId] ? (
                      <CircularProgress size={20} />
                    ) : budgets.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No linked budgets yet.
                      </Typography>
                    ) : (
                      <Stack spacing={1}>
                        {budgets.map((budget) => (
                          <Tooltip
                            title={`Total ${currencyFormatter.format(budget.totalBudget || 0)} • ${budget.period}`}
                            key={budget._id || budget.name}
                          >
                            <Chip
                              variant="outlined"
                              label={`${budget.name} (${budget.period})`}
                              color="primary"
                            />
                          </Tooltip>
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      <Divider sx={{ my: 3 }} />

      <Paper
        variant="outlined"
        sx={{
          p: 3,
          borderRadius: 3,
          bgcolor: 'grey.50'
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <VolunteerActivismIcon color="primary" fontSize="large" />
          <Box flex={1}>
            <Typography variant="subtitle1" fontWeight="bold">
              Invite friends & keep earning
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Budgit still rewards you when friends sign up. Share your referral link and you’ll both
              get perks once they upgrade.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="secondary"
            onClick={async () => {
              if (!referralLink) {
                try {
                  setReferralLoading(true);
                  const generated = await generateReferralCode();
                  setReferralLink(generated?.shareUrl || null);
                } finally {
                  setReferralLoading(false);
                }
              }
              if (referralLink) {
                try {
                  await navigator.clipboard.writeText(referralLink);
                  setInviteSuccess('Referral link copied! Share it with a friend to earn rewards.');
                } catch (_) {
                  setInviteError('Unable to copy referral link right now.');
                }
              }
            }}
            disabled={referralLoading}
          >
            {referralLoading ? 'Preparing link…' : 'Copy referral link'}
          </Button>
        </Stack>
        {referralLink && (
          <Typography variant="caption" sx={{ mt: 1, display: 'block', wordBreak: 'break-all' }}>
            {referralLink}
          </Typography>
        )}
      </Paper>
    </Paper>
  );
};

export default FamilyInviteSection;

